import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Package, Plus, Pencil, Trash2, X, Check, Calendar, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSpecialPackagesPage = () => {
  const { token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    days: 1,
    hours_per_day: 2,
    price: 0,
    is_active: true
  });

  const fetchPackages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/special-packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPackages(data);
      }
    } catch (error) {
      toast.error('Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const openCreateModal = () => {
    setEditingPackage(null);
    setFormData({
      name: '',
      description: '',
      days: 1,
      hours_per_day: 2,
      price: 0,
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      days: pkg.days,
      hours_per_day: pkg.hours_per_day,
      price: pkg.price,
      is_active: pkg.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Package name is required');
      return;
    }
    if (formData.price <= 0) {
      toast.error('Price must be greater than 0');
      return;
    }

    try {
      const url = editingPackage 
        ? `${API}/admin/special-packages/${editingPackage.package_id}`
        : `${API}/admin/special-packages`;
      
      const res = await fetch(url, {
        method: editingPackage ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingPackage ? 'Package updated successfully' : 'Package created successfully');
        setShowModal(false);
        fetchPackages();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save package');
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Are you sure you want to delete "${pkg.name}"?`)) {
      return;
    }

    try {
      const res = await fetch(`${API}/admin/special-packages/${pkg.package_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Package deleted successfully');
        fetchPackages();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete package');
      }
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const toggleActive = async (pkg) => {
    try {
      const res = await fetch(`${API}/admin/special-packages/${pkg.package_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !pkg.is_active })
      });

      if (res.ok) {
        toast.success(`Package ${pkg.is_active ? 'deactivated' : 'activated'}`);
        fetchPackages();
      }
    } catch (error) {
      toast.error('Failed to update package');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Special Packages</h1>
            <p className={`${theme.textMuted} mt-1`}>
              Manage promotional packages for customers
            </p>
          </div>
          <Button 
            onClick={openCreateModal}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="create-package-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Package
          </Button>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <div className={`text-center py-12 ${theme.textMuted}`}>Loading packages...</div>
        ) : packages.length === 0 ? (
          <Card className={`${theme.card} ${theme.border}`}>
            <CardContent className="py-12 text-center">
              <Package className={`h-12 w-12 mx-auto mb-4 ${theme.textMuted}`} />
              <p className={theme.textMuted}>No special packages yet</p>
              <p className={`text-sm ${theme.textSubtle} mt-1`}>Create your first package to offer promotions</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {packages.map(pkg => (
              <Card 
                key={pkg.package_id} 
                className={`${theme.card} ${theme.border} ${!pkg.is_active ? 'opacity-60' : ''}`}
                data-testid={`package-card-${pkg.package_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${pkg.is_active ? 'bg-orange-500/20' : isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        <Package className={`h-5 w-5 ${pkg.is_active ? 'text-orange-500' : theme.textMuted}`} />
                      </div>
                      <div>
                        <CardTitle className={`text-lg ${theme.text}`}>{pkg.name}</CardTitle>
                        {!pkg.is_active && (
                          <span className="text-xs text-rose-400">Inactive</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xl font-bold text-orange-500">
                      ₱{pkg.price.toLocaleString()}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pkg.description && (
                    <p className={`text-sm ${theme.textMuted}`}>{pkg.description}</p>
                  )}
                  
                  <div className="flex items-center gap-4">
                    <div className={`flex items-center gap-1 text-sm ${theme.textMuted}`}>
                      <Calendar className="h-4 w-4" />
                      <span>{pkg.days} day{pkg.days > 1 ? 's' : ''}</span>
                    </div>
                    <div className={`flex items-center gap-1 text-sm ${theme.textMuted}`}>
                      <Clock className="h-4 w-4" />
                      <span>{pkg.hours_per_day} hr{pkg.hours_per_day > 1 ? 's' : ''}/day</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-slate-700/50">
                    <Button
                      size="sm"
                      variant="ghost"
                      className={theme.textMuted}
                      onClick={() => openEditModal(pkg)}
                      data-testid={`edit-package-${pkg.package_id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className={pkg.is_active ? 'text-amber-400' : 'text-emerald-400'}
                      onClick={() => toggleActive(pkg)}
                    >
                      {pkg.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-400 ml-auto"
                      onClick={() => handleDelete(pkg)}
                      data-testid={`delete-package-${pkg.package_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className={`${theme.text} flex items-center gap-2`}>
                  <Package className="h-5 w-5 text-orange-500" />
                  {editingPackage ? 'Edit Package' : 'Create Package'}
                </CardTitle>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  onClick={() => setShowModal(false)}
                  className={theme.textMuted}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className={theme.text}>Package Name <span className="text-rose-400">*</span></Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., 1-Day Pass (2 Hours)"
                      className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                      data-testid="package-name-input"
                    />
                  </div>

                  <div>
                    <Label className={theme.text}>Description</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description of the package"
                      className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={theme.text}>Number of Days</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={formData.days}
                        onChange={(e) => setFormData(prev => ({ ...prev, days: parseInt(e.target.value) || 1 }))}
                        className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                        data-testid="package-days-input"
                      />
                    </div>
                    <div>
                      <Label className={theme.text}>Hours per Day</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={formData.hours_per_day}
                        onChange={(e) => setFormData(prev => ({ ...prev, hours_per_day: parseInt(e.target.value) || 1 }))}
                        className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                        data-testid="package-hours-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className={theme.text}>Price (₱) <span className="text-rose-400">*</span></Label>
                    <Input
                      type="number"
                      min="0"
                      step="50"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      className={`mt-1 ${isDarkMode ? 'bg-slate-900/50 border-slate-600' : ''}`}
                      data-testid="package-price-input"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="rounded"
                    />
                    <Label htmlFor="is_active" className={theme.text}>Active (visible to admins when booking)</Label>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="button"
                      variant="outline" 
                      className={`flex-1 ${isDarkMode ? 'border-slate-600' : 'border-gray-300'}`}
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="save-package-btn"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      {editingPackage ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSpecialPackagesPage;
